<template>
  <div>
    <PageAllow controller="Settlements" action="queueIndex" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              deleteIcon
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="queueCodeKeyWord"
                  v-model="options.queue_code"
                  @text="queueCodeKeyWord = $event"
                  :label="$t('SettlementQueue.Fields.queue_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.status"
                  :label="$t('SettlementQueue.status.title')"
                  :items="$consts.settlement.queueStatus"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="mx-4 my-4">
        <v-data-table
          :headers="headers"
          class="elevation-2 pa-4 mt-4"
          :items="settlementQueues"
          disable-sort
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :footer-props="footerProps"
          :loading="loading"
        >
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | settleQueueStatusColor"
              :text-color="item.status | settleQueueStatusTextColor"
            >
              {{ item.status | settleQueueStatusText }}
            </v-chip>
            <span v-if="item.cancel_flg" class="red--text">
              ({{$t("CRUD.cancelled")}})
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip top v-if="$permission.permissionAllow('Settlements', 'queueBills')">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  dark
                  color="info"
                  class="px-2 me-2 mb-1"
                  @click.stop="
                    current = item;
                    queueBillDialog = true
                  "
                >
                  <v-icon x-small class="pe-1">fa-align-left</v-icon>
                  {{ $t("SettlementQueue.Fields.details") }}
                </v-btn>
              </template>
              <span>{{ $t("SettlementQueue.Fields.details") }}</span>
            </v-tooltip>
            <v-tooltip top v-if="$permission.permissionAllow('Settlements', 'purchaseRegenerate') && item.actions && item.actions.regenerate">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  dark
                  color="action"
                  class="px-2 me-2 mb-1"
                  @click.stop="
                    current = item;
                    regenerateDialog = true
                  "
                >
                  {{ $t("SettlementQueue.Fields.regenerate") }}
                </v-btn>
              </template>
              <span>{{ $t("SettlementQueue.Fields.regenerate") }}</span>
            </v-tooltip>
            <v-tooltip top v-if="$permission.permissionAllow('Settlements', 'purchaseCancel') && item.actions && item.actions.cancel">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  dark
                  color="danger"
                  class="px-2 me-2 mb-1"
                  @click.stop="
                    current = item;
                    cancelDialog = true
                  "
                >
                  {{ $t("SettlementQueue.Fields.cancel") }}
                </v-btn>
              </template>
              <span>{{ $t("SettlementQueue.Fields.cancel") }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </div>
    </v-sheet>
    <SettlementQueueBillsDialog v-model="queueBillDialog" :queueCode="current && current.queue_code ? current.queue_code : ''"/>
    <DialogAlert
      v-model="regenerateDialog"
      :title="$t('SettlementQueue.regenerate')"
      :message="$t('SettlementQueue.regenerateConfirmMsg', [current && current.queue_code ? current.queue_code : ''])"
      useConfirm
      @confirm="regenerate(current.id)"
    />
    <DialogAlert
      v-model="cancelDialog"
      :title="$t('SettlementQueue.cancel')"
      :message="$t('SettlementQueue.cancelConfirmMsg', [current && current.queue_code ? current.queue_code : ''])"
      useConfirm
      @confirm="cancel(current.id)"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Settlements from "@/api/settlements";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import SettlementQueueBillsDialog from "@/components/plugins/SettlementQueueBillsDialog";
import DialogAlert from "@/components/plugins/DialogAlert";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";


const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  type: 2,
  status: [0, 1, 2, 3],
  queue_code: '',
};

export default {
  components: {
    SearcherKeyword,
    DialogAlert,
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherStatus,
    SettlementQueueBillsDialog,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.settlements"),
        disabled: false,
        href: "/settlements-purchase",
      },
      {
        text: i18n.t("Nav.settlementsPurchase"),
        disabled: false,
        href: "/settlements-purchase",
      },
      {
        text: i18n.t("SettlementQueue.objectName"),
        disabled: true,
        href: "/settlements-purchase",
      },
    ],

    headers() {
      var headers = [
        {
          text: i18n.t("SettlementQueue.Fields.queue_code"),
          align: "start",
          sortable: false,
          value: "queue_code",
        },
        {
          text: i18n.t("SettlementQueue.Fields.date"),
          align: "start",
          sortable: false,
          value: "date",
        },
        {
          text: i18n.t("SettlementQueue.Fields.status"),
          align: "start",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("SettlementQueue.Fields.created"),
          align: "start",
          sortable: false,
          value: "created",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "actions",
        },
      ];
      return headers;
    },

    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("StockOrderOUT.Fields.created_time"),
      },
    ],
    footerProps() {
      let props = Object.assign({}, i18n.t("$config.footer-props"));
      props.itemsPerPageOptions = [...props.itemsPerPageOptions, -1];
      return props;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["settings"]),
  },

  data: () => ({
    settlementQueues: [],
    paging: {},
    options: JSON.parse(JSON.stringify(defaultOptions)),
    loading: false,
    queueBillDialog: false,
    queueCodeKeyWord: '',
    current: null,
    regenerateDialog: false,
    cancelDialog: false,
  }),

  mounted() {
    if (this.$route.query.queue_code) {
      this.options.queue_code = this.$route.query.queue_code;
    }
  },

  methods: {
    resetSearch() {
      this.$route.query.queue_code = ''
      this.queueCodeKeyWord = '';
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.queueCodeKeyWord].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        queue_code: this.queueCodeKeyWord
      });
    },
    getPurchaseQueueIndex() {
      this.loading = true;
      let options = Object.assign({}, this.options, {
        itemsPerPage:
          this.options.itemsPerPage == -1
            ? this.$consts.limitAll
            : this.options.itemsPerPage,
      });
      Settlements.queueIndex(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.settlementQueues = response.data.settlement_queues;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
          this.loading = false;
        }.bind(this)
      );
    },
    regenerate(id) {
      Settlements.queueRegenerate(id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.$snackbar.success(i18n.t("SettlementQueue.regenerateSuccessMsg"));
            this.getPurchaseQueueIndex()
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
          this.loading = false;
        }.bind(this)
      );
    },
    cancel(id) {
      Settlements.queueCancel(id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.$snackbar.success(i18n.t("SettlementQueue.cancelSuccessMsg"));
            this.getPurchaseQueueIndex()
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
          this.loading = false;
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getPurchaseQueueIndex();
      },
    },
  },
};
</script>